<template>
  <v-dialog
    dense
    v-model="show"
    scrollable
  >
    <v-card
      height="62vh"
    >
      <v-card-title>
        These problems can't be resolved automatically
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-3">
        <v-data-table
          dense
          :headers="tableHeaders"
          :items="tableData"
          :items-per-page="itemsPerPage"
          :footer-props="{
            'items-per-page-options': [10, 30, 50, -1]
          }"
          @pagination="updateCurrentCount"
        >
          <template v-slot:[`item.Assignee_Display_Name`]="{ item }">
            <span v-if="editRow !== item.Problem_ID">{{ item.Assignee_Display_Name }}</span>
            <v-select
              class="pt-3"
              dense
              hide-details
              v-if="editRow === item.Problem_ID"
              v-model="item.Assignee_Display_Name"
              :items="employeeList"
              label="Employee"
              item-text="full_name"
              item-value="full_name"
            >
              <template v-slot:prepend-item>
                <div class="px-2">
                  <v-text-field
                    label="Search employee"
                    v-model="searchEmployee"
                  ></v-text-field>
                </div>
              </template>
            </v-select>
          </template>
          <template v-slot:[`item.Real_Dur`]="{ item }">
            {{ item.Real_Dur | convertToHours }}
          </template>
          <template v-slot:[`item.Actions`]="{ item, index }">
            <v-tooltip bottom color="primary" v-if="editRow === -1 || editRow !== index">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="editRow >= 0"
                  icon
                  color="primary"
                  @click="onEditRow(item)"
                >
                  <v-icon>mdi-pencil-circle</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip bottom color="primary" v-if="editRow === item.Problem_ID">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  v-bind="attrs"
                  v-on="on"
                  icon
                  color="primary"
                  @click="onCancelEdit(item)"
                >
                  <v-icon>mdi-close-circle</v-icon>
                </v-btn>
              </template>
              <span>Cancel</span>
            </v-tooltip>
            <v-tooltip bottom color="success" v-if="editRow === item.Problem_ID">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  v-bind="attrs"
                  v-on="on"
                  icon
                  color="success"
                  @click="onResolve(item)"
                >
                  <v-icon>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>Resolve</span>
            </v-tooltip>
            <v-tooltip bottom color="red darken-2" v-if="editRow === -1 || editRow !== index">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="editRow >= 0"
                  icon
                  color="red darken-2"
                  @click="onIgnore(item.Problem_ID)"
                >
                  <v-icon>mdi-alert-circle</v-icon>
                </v-btn>
              </template>
              <span>Ignore</span>
            </v-tooltip>
          </template>
          <!-- Footer with pagination info -->
          <template v-slot:footer>
            <v-row class="py-2">
              <v-col cols="auto" class="d-flex align-center">
                <span class="text-caption">Now shown {{ currentCount }} of {{ tableData.length }} problems</span>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-switch
                  class="my-0"
                  v-model="showAllItems"
                  label="Show all problems"
                  color="primary"
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          small
          color="primary"
          @click="show = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'JiraImportProblemsListDialog',
  props: {
    value: {
      required: true
    },
    problems: {
      required: false
    }
  },
  data () {
    return {
      searchEmployee: '',
      editRow: -1,
      itemCopy: {},
      currentCount: 0,
      showAllItems: false,
      currentItemsPerPage: 10,
      tableHeaders: [
        {
          text: 'Assignee',
          value: 'Assignee_Display_Name',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '20%'
        },
        {
          text: 'Jira Issue Title',
          value: 'Jira_Issue_Name',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '20%'
        },
        {
          text: 'Report Duration',
          value: 'Real_Dur',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '20%'
        },
        {
          text: 'Report Date',
          value: 'Start_Date',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          divider: true,
          width: '20%'
        },
        {
          text: '#',
          align: 'center',
          value: 'Actions',
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap',
          sortable: false,
          width: '20%'
        }
      ]
    }
  },
  computed: {
    itemsPerPage: {
      get () {
        return this.showAllItems ? -1 : this.currentItemsPerPage
      },
      set (val) {
        this.currentItemsPerPage = val
      }
    },
    employeeList () {
      const empl = this.$store.getters.getEmployee
      if (this.searchEmployee) {
        const reg = new RegExp(this.searchEmployee.toLowerCase(), 'gi')
        return empl.filter(item => {
          reg.lastIndex = 0
          return reg.test(item.full_name.toLowerCase())
        })
      } else return empl
    },
    show: {
      get () {
        if (this.value && !this.$store.getters.getEmployee.length) {
          this.getEmployeeList()
        }
        return this.value
      },
      set (val) {
        if (!val) {
          this.editRow = -1
          this.itemCopy = {}
          this.showAllItems = false
          this.currentItemsPerPage = 10
        }
        this.$emit('input', val)
      }
    },
    tableData () {
      return this.problems.map(item => {
        return {
          Problem_ID: item.Problem_ID,
          ...item.Problem_Data
        }
      })
    }
  },
  filters: {
    convertToHours (val) {
      return +val / 60 + 'H'
    }
  },
  methods: {
    updateCurrentCount (e) {
      this.currentCount = e.itemsLength
    },
    getEmployeeList () {
      this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    onEditRow (item) {
      this.searchEmployee = item.Assignee_Display_Name
      this.editRow = item.Problem_ID
      this.itemCopy = Object.assign({}, this.problems.filter(i => i.Problem_ID === item.Problem_ID)[0])
    },
    onCancelEdit (item) {
      const editableItemInd = this.problems.findIndex(i => i.Problem_ID === item.Problem_ID)
      this.$set(this.problems, editableItemInd, this.itemCopy)
      this.itemCopy = {}
      this.editRow = -1
      this.searchEmployee = ''
    },
    onResolve (item) {
      const editableItemInd = this.problems.findIndex(i => i.Problem_ID === item.Problem_ID)
      this.itemCopy.Problem_Data = {
        Assignee_Display_Name: item.Assignee_Display_Name,
        Description: item.Description,
        Integration_ID: item.Integration_ID,
        Jira_Issue_ID: item.Jira_Issue_ID,
        Jira_Issue_Key: item.Jira_Issue_Key,
        Jira_Issue_Name: item.Jira_Issue_Name,
        Jira_Report_ID: item.Jira_Report_ID,
        Project_ID: item.Project_ID,
        Real_Dur: item.Real_Dur,
        Report_Dur: item.Report_Dur,
        Start_Date: item.Start_Date
      }
      this.$set(this.problems, editableItemInd, this.itemCopy)
      this.$emit('resolveProblem', this.itemCopy)
      this.itemCopy = {}
      this.editRow = -1
      this.searchEmployee = ''
    },
    onIgnore (problemID) {
      this.$emit('ignoreProblem', problemID)
    }
  }
}
</script>
